import * as React from 'react';

import { observer } from 'mobx-react';

import { ITranslationsCollection } from '@estee/elc-service';
import { Tab, Tabs } from '@estee/elc-tabs';

import { AccountAccessPageTabId } from '~constants/Constants';
import { SignUpType } from '~constants/SignUpTypeEnum';
import { View } from '~domain/entities/SignInWithOtpFormStore';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/RampService';
import { classes } from '~utils/Classes';
import { IamForms, useConfig } from '~utils/hooks/config';
import { useTranslation } from '~utils/hooks/translations';
import { EmailForm } from '~views/email/EmailForm';
import { GuestCheckoutForm } from '~views/guest/GuestCheckoutForm';
import { Otp } from '~views/otp/Otp';
import { ResetPasswordForm } from '~views/resetPassword/ResetPasswordForm';
import { SignUp } from '~views/signup/SignUp';

import { AccountSignInForm } from './AccountSignInForm';

export interface IAccountAccessPageProps {
    translations?: ITranslationsCollection;
    redirectUrl?: string;
}

const tabsClassName = classes('iam-access-page-tabs');
const tabBaseName = 'iam-access-page-tab';

export const AccountAccessPage = observer((props: IAccountAccessPageProps) => {
    const viewController = diContainer.get(serviceNames.accountAccessViewController);
    const {
        onSelectTab,
        data: {
            currentTabId,
            isSocialSignUpFlow,
            isGuestAndSignIn,
            showOtp,
            view,
            showSignInFormOnly
        }
    } = viewController;
    const {
        signInLabel,
        guestCheckoutLabel,
        createAccountLabel,
        continueCheckoutLabel
    } = useTranslation();
    const guestCheckout = useConfig(IamForms.guestCheckout);
    const showGuestCheckoutForm = isGuestAndSignIn && !!guestCheckout;

    if (view === View.PASSWORD) {
        return <ResetPasswordForm />;
    }

    if (view === View.EMAIL) {
        return <EmailForm />;
    }

    if (showOtp) {
        return <Otp />;
    }

    if (showSignInFormOnly) {
        return <AccountSignInForm />;
    }

    return (
        <>
            <Tabs currentTabId={currentTabId} className={tabsClassName} onSelect={onSelectTab}>
                {showGuestCheckoutForm && (
                    <Tab
                        id={AccountAccessPageTabId.GUEST_CHECKOUT}
                        title={guestCheckoutLabel}
                        className={classes(
                            tabBaseName,
                            `${tabBaseName}-${AccountAccessPageTabId.GUEST_CHECKOUT}`
                        )}
                    >
                        <GuestCheckoutForm />
                    </Tab>
                )}
                <Tab
                    id={AccountAccessPageTabId.SIGN_IN}
                    title={signInLabel}
                    className={classes(
                        tabBaseName,
                        `${tabBaseName}-${AccountAccessPageTabId.SIGN_IN}`
                    )}
                >
                    <AccountSignInForm
                        submitButtonLabel={
                            showGuestCheckoutForm ? continueCheckoutLabel : undefined
                        }
                    />
                </Tab>
                {!showGuestCheckoutForm && !isSocialSignUpFlow && (
                    <Tab
                        id={AccountAccessPageTabId.SIGN_UP}
                        title={createAccountLabel}
                        className={classes(
                            tabBaseName,
                            `${tabBaseName}-${AccountAccessPageTabId.SIGN_UP}`
                        )}
                    >
                        <SignUp
                            submitButtonLabel={createAccountLabel}
                            signUpType={SignUpType.COMPLETE}
                        />
                    </Tab>
                )}
            </Tabs>
        </>
    );
});

export default AccountAccessPage;
